import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Loader } from "../../components/common/loader";
import { getTransactionHistoryService, getRpaTransactionHistoryService } from "../../api/transactionApi";
import "./style.css";

const STORAGE_KEY = "txn_history_mobile";
const PAGE_SIZE = 5;

const titleCase = (s) => {
  if (!s) return "";
  return String(s)
    .split(" ")
    .map((w) => (w ? w[0].toUpperCase() + w.substring(1).toLowerCase() : w))
    .join(" ");
};

const formatDate = (raw) => {
  if (!raw) return "";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

const statusClass = (status) => {
  const s = (status || "").toUpperCase();
  if (s === "PURCHASED") return "purchased";
  if (s === "CHECKOUT") return "checkout";
  if (s === "QUOTE" || s === "BUY-NOW") return "quote";
  return "default";
};

const StatusChip = ({ status }) => (
  <span className={`txn-status ${statusClass(status)}`}>
    {(status || "").toUpperCase()}
  </span>
);

const TransactionCard = ({ record, onView, t }) => {
  const product = titleCase(record.product_name);
  const company = titleCase(record.insurance_company);
  const isTravel = record.product_id === 15;
  const isMotor = record.product_id === 1;

  return (
    <div className="txn-card txn-list-card" onClick={onView}>
      <div className="txn-list-head">
        <div>
          <div className="txn-product-name">
            {product} {t("transaction_history.insurance")}
          </div>
          {company && <div className="txn-company-name">{company}</div>}
        </div>
        <StatusChip status={record.business_status} />
      </div>

      {record.policy_no && (
        <div className="txn-row">
          <span className="txn-row-label">{t("transaction_history.policy_no")}</span>
          <span className="txn-row-value">{record.policy_no}</span>
        </div>
      )}
      {record.quote_no && (
        <div className="txn-row">
          <span className="txn-row-label">{t("transaction_history.quote_no")}</span>
          <span className="txn-row-value">{record.quote_no}</span>
        </div>
      )}
      {isTravel && (record.travel_from || record.travel_to) && (
        <div className="txn-row">
          <span className="txn-row-label">{t("transaction_history.travel")}</span>
          <span className="txn-row-value">
            {record.travel_from || ""} → {record.travel_to || ""}
          </span>
        </div>
      )}
      {isMotor && record.plate_no && (
        <div className="txn-row">
          <span className="txn-row-label">{t("transaction_history.plate_no")}</span>
          <span className="txn-row-value">
            {[record.plate_character, record.plate_no]
              .filter(Boolean)
              .join(" ")}
          </span>
        </div>
      )}
      {record.txn_date && (
        <div className="txn-row">
          <span className="txn-row-label">{t("transaction_history.txn_date")}</span>
          <span className="txn-row-value">{formatDate(record.txn_date)}</span>
        </div>
      )}
      <div className="txn-row">
        <span className="txn-row-label">{t("transaction_history.premium")}</span>
        <span className="txn-row-value">
          {record.premium_amount} {t("transaction_history.omr")}
        </span>
      </div>

      <div className="txn-view-link">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
        >
          {t("transaction_history.view_details")} →
        </button>
      </div>
    </div>
  );
};

const TransactionHistory = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [mobile, setMobile] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [records, setRecords] = useState(null);
  const [allRecords, setAllRecords] = useState(null);
  const [serverPaginated, setServerPaginated] = useState(false);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [activeTab, setActiveTab] = useState("transactions");
  const [rpaRecords, setRpaRecords] = useState(null);
  const [rpaAllRecords, setRpaAllRecords] = useState(null);
  const [rpaPage, setRpaPage] = useState(1);
  const [rpaLastPage, setRpaLastPage] = useState(1);
  const [rpaTotal, setRpaTotal] = useState(0);
  const [rpaServerPaginated, setRpaServerPaginated] = useState(false);
  const [rpaLoading, setRpaLoading] = useState(false);

  const fetchHistory = async (mobileNo, requestedPage = 1) => {
    setLoading(true);
    setError("");
    try {
      const res = await getTransactionHistoryService(mobileNo, {
        page: requestedPage,
        perPage: PAGE_SIZE,
      });
      const data = res?.data;

      // Server-paginated response: { data: [...], current_page, last_page, total }
      if (data && Array.isArray(data.data) && data.last_page !== undefined) {
        setServerPaginated(true);
        setRecords(data.data);
        setAllRecords(null);
        setPage(data.current_page || requestedPage);
        setLastPage(data.last_page || 1);
        setTotal(data.total ?? data.data.length);
      } else {
        // Bare array — paginate client-side.
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
            ? data.data
            : [];
        setServerPaginated(false);
        setAllRecords(items);
        setPage(1);
        setLastPage(Math.max(1, Math.ceil(items.length / PAGE_SIZE)));
        setTotal(items.length);
        setRecords(items.slice(0, PAGE_SIZE));
      }
      setSearch("");
      localStorage.setItem(STORAGE_KEY, mobileNo);
      setSubmitted(true);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          err?.message ||
          t("transaction_history.fetch_failed")
      );
      setSubmitted(true);
      setRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && saved.trim()) {
      setMobile(saved);
      fetchHistory(saved, 1);
    }
  }, []);

  // Client-side pagination slice when backend returns full array.
  useEffect(() => {
    if (!serverPaginated && allRecords) {
      const start = (page - 1) * PAGE_SIZE;
      setRecords(allRecords.slice(start, start + PAGE_SIZE));
    }
  }, [page, serverPaginated, allRecords]);

  useEffect(() => {
    if (!rpaServerPaginated && rpaAllRecords) {
      const start = (rpaPage - 1) * PAGE_SIZE;
      setRpaRecords(rpaAllRecords.slice(start, start + PAGE_SIZE));
    }
  }, [rpaPage, rpaServerPaginated, rpaAllRecords]);

  const goToPage = (next) => {
    if (next < 1 || next > lastPage || next === page) return;
    if (serverPaginated) {
      fetchHistory(mobile.trim(), next);
    } else {
      setPage(next);
    }
  };

  const filteredRecords = (() => {
    const activeRecords = activeTab === "rpa" ? rpaRecords : records;
    if (!activeRecords) return [];
    const q = search.trim().toLowerCase();
    if (!q) return activeRecords;
    return activeRecords.filter((r) => {
      const haystack = [
        r.policy_no,
        r.quote_no,
        r.plate_no,
        r.plate_character,
        r.insurance_company,
        r.product_name,
        r.business_status,
        r.payment_status,
        r.travel_from,
        r.travel_to,
        r.mobile_no,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  })();

  const onSubmit = (e) => {
    e.preventDefault();
    const v = mobile.trim();
    if (!v) {
      toast.error(t("transaction_history.mobile_required"));
      return;
    }
    if (v.length < 8) {
      toast.error(t("transaction_history.mobile_invalid"));
      return;
    }
    fetchHistory(v, 1);
  };

  const fetchRpaHistory = async (mobileNo, requestedPage = 1) => {
    setRpaLoading(true);
    try {
      const res = await getRpaTransactionHistoryService(mobileNo, {
        page: requestedPage,
        perPage: PAGE_SIZE,
      });
      const data = res?.data;
      if (data && Array.isArray(data.data) && data.last_page !== undefined) {
        setRpaServerPaginated(true);
        setRpaRecords(data.data);
        setRpaAllRecords(null);
        setRpaPage(data.current_page || requestedPage);
        setRpaLastPage(data.last_page || 1);
        setRpaTotal(data.total ?? data.data.length);
      } else {
        const items = Array.isArray(data)
          ? data
          : Array.isArray(data?.data)
          ? data.data
          : [];
        setRpaServerPaginated(false);
        setRpaAllRecords(items);
        setRpaPage(1);
        setRpaLastPage(Math.max(1, Math.ceil(items.length / PAGE_SIZE)));
        setRpaTotal(items.length);
        setRpaRecords(items.slice(0, PAGE_SIZE));
      }
    } catch (err) {
      setRpaRecords([]);
    } finally {
      setRpaLoading(false);
    }
  };

  const onSwitchNumber = () => {
    setMobile("");
    setRecords(null);
    setAllRecords(null);
    setSubmitted(false);
    setError("");
    setSearch("");
    setPage(1);
    setLastPage(1);
    setTotal(0);
    setRpaRecords(null);
    setRpaAllRecords(null);
    setRpaPage(1);
    setRpaLastPage(1);
    setRpaTotal(0);
    setActiveTab("transactions");
    localStorage.removeItem(STORAGE_KEY);
  };

  const onView = (record) => {
    navigate("/transaction-history/detail", { state: { record } });
  };

  if (loading || rpaLoading) return <Loader type="logo-gif" />;

  return (
    <section className="txn-history-page">
      <div className="container">
        {!submitted || (records === null && !error) ? (
          <div className="txn-card txn-form-card">
            <h2>{t("transaction_history.title")}</h2>
            <p className="txn-desc">{t("transaction_history.description")}</p>
            <form onSubmit={onSubmit}>
              <label className="form-label">
                {t("transaction_history.mobile_label")}
              </label>
              <input
                type="tel"
                inputMode="numeric"
                className="form-control"
                placeholder={t("transaction_history.mobile_hint")}
                value={mobile}
                onChange={(e) =>
                  setMobile(e.target.value.replace(/[^0-9]/g, ""))
                }
              />
              <button
                type="submit"
                className="txn-btn-gold"
                disabled={mobile.trim().length < 8}
              >
                {t("transaction_history.view_history")}
              </button>
            </form>
          </div>
        ) : error ? (
          <div className="txn-card">
            <div className="txn-error">
              <div className="icon">!</div>
              <p>{error}</p>
              <button
                type="button"
                className="txn-btn-gold"
                style={{ maxWidth: 220, margin: "12px auto 0" }}
                onClick={onSwitchNumber}
              >
                {t("transaction_history.try_another")}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="txn-header-row">
              <h3>
                {t("transaction_history.transactions_count", {
                  count: activeTab === "rpa" ? rpaTotal : total,
                })}
              </h3>
              <button
                type="button"
                className="txn-switch-btn"
                onClick={onSwitchNumber}
              >
                {t("transaction_history.switch_number")}
              </button>
            </div>

            {/* Tabs — always visible after submission */}
            <div className="txn-tabs">
              <button
                type="button"
                className={`txn-tab-btn ${activeTab === "transactions" ? "txn-tab-active" : ""}`}
                onClick={() => { setActiveTab("transactions"); setSearch(""); }}
              >
                Transactions
              </button>
              <button
                type="button"
                className={`txn-tab-btn ${activeTab === "rpa" ? "txn-tab-active" : ""}`}
                onClick={() => {
                  setActiveTab("rpa");
                  setSearch("");
                  if (rpaRecords === null) fetchRpaHistory(mobile.trim(), 1);
                }}
              >
                RPA Transactions
              </button>
            </div>

            {/* Search */}
            <div className="txn-search-wrap">
              <input
                type="text"
                className="form-control txn-search-input"
                placeholder={t("transaction_history.search_placeholder")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/* Records */}
            {filteredRecords.length === 0 ? (
              <div className="txn-card">
                <div className="txn-empty">
                  <div className="icon">🧾</div>
                  <p>
                    {t("transaction_history.no_transactions")} {mobile}
                  </p>
                </div>
              </div>
            ) : (
              filteredRecords.map((record) => (
                <TransactionCard
                  key={record.id}
                  record={record}
                  t={t}
                  onView={() => onView(record)}
                />
              ))
            )}

            {/* Pagination */}
            {activeTab === "rpa" ? (
              rpaLastPage > 1 && (
                <div className="txn-pagination">
                  <button
                    type="button"
                    className="txn-page-btn"
                    disabled={rpaPage <= 1}
                    onClick={() => {
                      const next = rpaPage - 1;
                      if (rpaServerPaginated) fetchRpaHistory(mobile.trim(), next);
                      else setRpaPage(next);
                    }}
                  >
                    ← {t("transaction_history.prev")}
                  </button>
                  <span className="txn-page-info">
                    {t("transaction_history.page_of", { current: rpaPage, total: rpaLastPage })}
                  </span>
                  <button
                    type="button"
                    className="txn-page-btn"
                    disabled={rpaPage >= rpaLastPage}
                    onClick={() => {
                      const next = rpaPage + 1;
                      if (rpaServerPaginated) fetchRpaHistory(mobile.trim(), next);
                      else setRpaPage(next);
                    }}
                  >
                    {t("transaction_history.next")} →
                  </button>
                </div>
              )
            ) : (
              lastPage > 1 && (
                <div className="txn-pagination">
                  <button
                    type="button"
                    className="txn-page-btn"
                    disabled={page <= 1}
                    onClick={() => goToPage(page - 1)}
                  >
                    ← {t("transaction_history.prev")}
                  </button>
                  <span className="txn-page-info">
                    {t("transaction_history.page_of", { current: page, total: lastPage })}
                  </span>
                  <button
                    type="button"
                    className="txn-page-btn"
                    disabled={page >= lastPage}
                    onClick={() => goToPage(page + 1)}
                  >
                    {t("transaction_history.next")} →
                  </button>
                </div>
              )
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default TransactionHistory;
